/**
 * Silence removal done on decoded audio in JavaScript. ffmpeg.wasm ships FFmpeg 5.1, where the
 * silenceremove filter's start/stop options behave differently from current FFmpeg and ended up
 * deleting speech, so detection and cutting are done here and ffmpeg is only used to encode.
 */

import { formatDuration } from "./timeFormat";

const WINDOW_SECONDS = 0.02;
/** Quiet kept beside speech in "shorten pauses" mode (it counts towards the pause length chosen). */
const PAD_SECONDS = 0.15;
/**
 * Quiet kept beside speech when pauses are removed completely: one analysis window or less, so a word's soft end is
 * not clipped but no pause of 0.1 s or more can be left behind. (It used to be PAD_SECONDS on each side, which left
 * 0.30 s of silence at every cut and a 2.3 s result where about 2.0 s was promised.)
 */
const COMPLETE_PAD_SECONDS = 0.015;
const FADE_SECONDS = 0.008;

export interface SilenceResult {
  /** One Float32Array per channel, with the quiet stretches removed. */
  channels: Float32Array[];
  keptSeconds: number;
  removedSeconds: number;
  pausesRemoved: number;
}

export interface Pause {
  /** The whole quiet stretch, in samples [start, end). */
  start: number;
  end: number;
  startSeconds: number;
  endSeconds: number;
  durationSeconds: number;
  /** The part of it that is cut in the current mode, in samples, or null when it is left as it is (already no longer than the pause length chosen). */
  cut: [number, number] | null;
}

/**
 * Every quiet stretch of at least minDuration seconds, with what would be cut from each. The list is what the
 * tool shows ("0:01 to 0:03") so a deliberate pause can be left in; removeSilence cuts the same ranges.
 */
export function detectPauses(
  channels: Float32Array[],
  sampleRate: number,
  thresholdDb: number,
  minDurationSeconds: number,
  keepPauseSeconds = 0
): Pause[] {
  const length = channels[0]?.length ?? 0;
  const win = Math.max(1, Math.round(sampleRate * WINDOW_SECONDS));
  const nWin = Math.ceil(length / win);
  const silent = new Array<boolean>(nWin);
  const threshold = Math.pow(10, thresholdDb / 20);
  for (let w = 0; w < nWin; w++) {
    const from = w * win;
    const to = Math.min(length, from + win);
    let loudest = 0;
    for (const ch of channels) {
      let sum = 0;
      for (let i = from; i < to; i++) sum += ch[i] * ch[i];
      const rms = Math.sqrt(sum / Math.max(1, to - from));
      if (rms > loudest) loudest = rms;
    }
    silent[w] = loudest < threshold;
  }

  const minWindows = Math.max(1, Math.round(minDurationSeconds / WINDOW_SECONDS));
  const pad = Math.round(sampleRate * (keepPauseSeconds > 0 ? PAD_SECONDS : COMPLETE_PAD_SECONDS));
  const pauses: Pause[] = [];
  let w = 0;
  while (w < nWin) {
    if (!silent[w]) { w++; continue; }
    let end = w;
    while (end < nWin && silent[end]) end++;
    if (end - w >= minWindows) {
      const runStart = w * win;
      const runEnd = Math.min(length, end * win);
      // Keep a little of the quiet on the side next to speech so words are not clipped.
      const from = runStart === 0 ? 0 : runStart + pad;
      const to = runEnd >= length ? length : runEnd - pad;
      // "Shorten pauses" mode: leave a natural pause of keepPauseSeconds (the pad on each side counts towards it).
      const extra = Math.max(0, Math.round(sampleRate * keepPauseSeconds) - 2 * pad);
      const f2 = runStart === 0 ? from : from + Math.floor(extra / 2);
      const t2 = runEnd >= length ? to : to - Math.ceil(extra / 2);
      pauses.push({
        start: runStart,
        end: runEnd,
        startSeconds: runStart / sampleRate,
        endSeconds: runEnd / sampleRate,
        durationSeconds: (runEnd - runStart) / sampleRate,
        cut: t2 - f2 > 0 ? [f2, t2] : null,
      });
    }
    w = end;
  }
  return pauses;
}

/** Ranges [startSample, endSample) that are removed: quiet for at least minDuration seconds. */
export function findRemovedRanges(
  channels: Float32Array[],
  sampleRate: number,
  thresholdDb: number,
  minDurationSeconds: number,
  keepPauseSeconds = 0
): Array<[number, number]> {
  return detectPauses(channels, sampleRate, thresholdDb, minDurationSeconds, keepPauseSeconds)
    .flatMap((p) => (p.cut ? [p.cut] : []));
}

/**
 * Cuts the pauses out. `leave` holds the `start` sample of pauses the person un-ticked: those stay in the audio
 * exactly as they were. pausesRemoved counts only pauses that were actually cut.
 */
export function removeSilence(
  channels: Float32Array[],
  sampleRate: number,
  thresholdDb: number,
  minDurationSeconds: number,
  keepPauseSeconds = 0,
  leave: ReadonlySet<number> = new Set()
): SilenceResult {
  const length = channels[0]?.length ?? 0;
  const removed = detectPauses(channels, sampleRate, thresholdDb, minDurationSeconds, keepPauseSeconds)
    .flatMap((p) => (p.cut && !leave.has(p.start) ? [p.cut] : []));
  const keep: Array<[number, number]> = [];
  let cursor = 0;
  for (const [from, to] of removed) {
    if (from > cursor) keep.push([cursor, from]);
    cursor = to;
  }
  if (cursor < length) keep.push([cursor, length]);

  const total = keep.reduce((n, [a, b]) => n + (b - a), 0);
  const fade = Math.round(sampleRate * FADE_SECONDS);
  const out = channels.map(() => new Float32Array(total));
  let pos = 0;
  keep.forEach(([a, b], idx) => {
    const n = b - a;
    channels.forEach((ch, c) => {
      out[c].set(ch.subarray(a, b), pos);
      // Short fades only where a cut was made, to avoid clicks.
      const f = Math.min(fade, Math.floor(n / 2));
      if (idx > 0) for (let i = 0; i < f; i++) out[c][pos + i] *= i / f;
      if (idx < keep.length - 1) for (let i = 0; i < f; i++) out[c][pos + n - 1 - i] *= i / f;
    });
    pos += n;
  });

  return {
    channels: out,
    keptSeconds: total / sampleRate,
    removedSeconds: (length - total) / sampleRate,
    pausesRemoved: removed.length,
  };
}

/**
 * The result line under the player: lengths to 0.1 s, how many pauses were cut, and which mode did it, so the
 * words match what was done ("1 pause removed" / "1 pause shortened to 0.3 s"), with a count of pauses kept.
 */
export function describeSilenceResult(o: { beforeSeconds: number; afterSeconds: number; cut: number; kept: number; keepPauseSeconds: number }): string {
  const noun = (n: number) => (n === 1 ? "pause" : "pauses");
  const what = o.keepPauseSeconds > 0 ? `${o.cut} ${noun(o.cut)} shortened to ${o.keepPauseSeconds} s` : `${o.cut} ${noun(o.cut)} removed`;
  return `${formatDuration(o.beforeSeconds)} \u2192 ${formatDuration(o.afterSeconds)}, ${what}${o.kept > 0 ? `, ${o.kept} kept` : ""}`;
}

/**
 * Encoder arguments that make the saved file use the sample rate of the file that was dropped. The decoder is asked
 * for that rate already; when a browser decoded at another one (decodedRate differs from the file's own rate), the
 * encode step converts back with -ar so the output never silently changes rate.
 */
export function rateKeepingCodec(codec: string[], decodedRate: number, fileRate: number | null): string[] {
  return fileRate && fileRate !== decodedRate ? [...codec, "-ar", String(fileRate)] : codec;
}

/** Length in seconds of the audio once the pauses not in `leave` are cut (what the tool shows before anything is processed). */
export function predictedSeconds(pauses: Pause[], leave: ReadonlySet<number>, totalSamples: number, sampleRate: number): number {
  const cutSamples = pauses.reduce((n, p) => n + (p.cut && !leave.has(p.start) ? p.cut[1] - p.cut[0] : 0), 0);
  return (totalSamples - cutSamples) / sampleRate;
}

/** 16-bit PCM WAV bytes, so ffmpeg can encode the result to any format. */
export function encodeWav(channels: Float32Array[], sampleRate: number): Uint8Array {
  const nCh = channels.length;
  const length = channels[0]?.length ?? 0;
  const dataBytes = length * nCh * 2;
  const buf = new ArrayBuffer(44 + dataBytes);
  const v = new DataView(buf);
  const str = (o: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  str(0, "RIFF"); v.setUint32(4, 36 + dataBytes, true); str(8, "WAVE"); str(12, "fmt ");
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, nCh, true);
  v.setUint32(24, sampleRate, true); v.setUint32(28, sampleRate * nCh * 2, true);
  v.setUint16(32, nCh * 2, true); v.setUint16(34, 16, true); str(36, "data"); v.setUint32(40, dataBytes, true);
  let o = 44;
  for (let i = 0; i < length; i++) {
    for (let c = 0; c < nCh; c++) {
      const s = Math.max(-1, Math.min(1, channels[c][i]));
      v.setInt16(o, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      o += 2;
    }
  }
  return new Uint8Array(buf);
}
